import * as React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import {
  LocationsListNoLink,
  SidebarBox,
  SvgSprite,
  VolunteerSearchLink,
} from "@skv/core";

import { opportunityStore } from "@skv/state";
import { getDateRange } from "@skv/util";
import { ShareSocialDropdown } from "~/ui/ShareSocial";

const OppListSvgSprite = styled(SvgSprite)`
  vertical-align: text-top;
  margin: 0.15rem 0.35rem 0 0.35rem;
`;

const Block = styled(SidebarBox)`
  background-color: ${(p) => p.theme.gray200};
  border: none;
  padding: 1rem;
  margin-bottom: 1rem;
  p:last-child {
    margin-bottom: 0;
  }
`;

const OpportunityDetailsAside = (title): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { data } = opportunityStore.hooks.useOpportunity(id);
  const {
    locations,
    category,
    typesOfWork,
    suitableFors,
    commitment,
    training,
    timeRequired,
    reimbursement,
    requirements,
    requirementDescription,
    onlineOrRemote,
    eventStartDate,
    eventEndDate,
  } = data;

  return (
    <aside className="col-md-4 ml-auto mb-4">
      <ShareSocialDropdown
        showEmail
        showFacebook
        showLinkedin
        showTwitter
        emailOptions={{
          subject: title,
        }}
      />
      <SidebarBox
        css={`
          border-color: transparent;
          margin-top: 0;
          padding-top: 0;
        `}
      >
        <ul
          css={`
            > li {
              margin-bottom: 0.5rem;
            }
          `}
          className="list-unstyled"
        >
          <li>
            <OppListSvgSprite
              id="icons:marker"
              width={16}
              height={16}
              aria-label="Location: "
            />
            <LocationsListNoLink
              locations={locations}
              onlineOrRemote={onlineOrRemote}
            />
          </li>
          <li>
            <OppListSvgSprite
              id={eventStartDate ? "icons:clock" : "icons:calendar"}
              width={16}
              height={16}
              aria-label="Commitment: "
            />
            {commitment}
          </li>
          <li>
            <OppListSvgSprite
              id="icons:ribbon"
              width={16}
              height={16}
              aria-label="Category: "
            />
            {category.name}
          </li>
          {eventStartDate && (
            <li>
              <OppListSvgSprite
                id="icons:calendar"
                width={16}
                height={16}
                aria-label="Event date: "
              />
              {getDateRange(eventStartDate, eventEndDate, "D MMMM YYYY")}
            </li>
          )}
        </ul>
      </SidebarBox>

      {!!typesOfWork.length && (
        <Block>
          <h2 className="h4 text-reset">Type of work</h2>
          <p>
            {typesOfWork.map((name, idx) => (
              <React.Fragment key={idx}>
                {idx >= 1 ? ", " : null} <span>{name}</span>
              </React.Fragment>
            ))}
          </p>
        </Block>
      )}

      {!!suitableFors.length && (
        <Block>
          <h2 className="h4 text-reset">Suitable for</h2>
          <p>
            {suitableFors.map((name, idx) => (
              <React.Fragment key={name}>
                {idx >= 1 ? ", " : null} <span>{name}</span>
              </React.Fragment>
            ))}
          </p>
        </Block>
      )}

      {training && (
        <Block>
          <h2 className="h4 text-reset">Training</h2>
          <p>{training}</p>
        </Block>
      )}

      {timeRequired && (
        <Block>
          <h2 className="h4 text-reset">Time required</h2>
          <p>{timeRequired}</p>
        </Block>
      )}

      {reimbursement && (
        <Block>
          <h2 className="h4 text-reset">Reimbursement</h2>
          <p>{reimbursement}</p>
        </Block>
      )}

      {/* Requirements */}
      {((): React.ReactNode => {
        if (requirementDescription || !!requirements.length) {
          const hasAgencySupportedRequirements = requirements.some(
            (req) => req.agencySupport
          );

          return (
            <Block>
              <h2 className="h4 text-reset">Requirements</h2>

              {!!requirements.length && (
                <ul className="list-unstyled">
                  {requirements.map((requirement) => (
                    <li key={requirement.id}>
                      {requirement.name}{" "}
                      {requirement.agencySupport && <sup>*</sup>}
                    </li>
                  ))}
                </ul>
              )}

              {!!requirementDescription && (
                <p className="text-break">{requirementDescription}</p>
              )}

              {hasAgencySupportedRequirements && (
                <p>
                  * May be arranged via the Organisation. Details to be provided
                  on expression of interest.
                </p>
              )}
            </Block>
          );
        }

        return null;
      })()}

      <VolunteerSearchLink
        className="link"
        to={`/volunteering?organisationId=${data.organisation.id}`}
        searchQuery={{
          organisationId: `${data.organisation.id}`,
        }}
        searchType="opp"
      >
        See all opportunities from {data.organisation.name}
      </VolunteerSearchLink>
    </aside>
  );
};

export default OpportunityDetailsAside;
